import { motion } from 'framer-motion'
import type { ParseKeys } from 'i18next'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { TAB_MARKER_TRANSITION } from '@/lib/motion'

/* A tab is labelled either by a translation key or by a literal string. Both
 * are accepted because the i18n migration is in flight — Directories already
 * carries labelKey while Money and Settings still carry label — and a switcher
 * that insisted on one of them would have to land in the same commit as the
 * translation of every page that uses it. `labelKey` wins where both appear.
 *
 * ParseKeys, not string: i18next.d.ts types `t` against the English catalogue
 * precisely so a mistyped key fails the build instead of rendering a raw dotted
 * key to a user. Widening to string here would punch a hole in that for every
 * tab in the app. */
export type TabNavItem<T extends string> = {
  key: T
  /** A count shown after the label — pending files, open approvals — or
   *  `true` for a marker with no count (a dot: something is still missing). */
  badge?: number | true
  badgeTone?: 'neutral' | 'attention'
  /** What the badge means, in words: spoken with the label and shown as the
   *  badge's tooltip. A bare "3" or a dot says nothing on its own, and colour
   *  never carries meaning alone. */
  badgeLabel?: string
} & (
  | { label: string; labelKey?: never }
  | { labelKey: ParseKeys; label?: never }
)

export interface TabNavProps<T extends string> {
  items: readonly TabNavItem<T>[]
  value: T
  onChange: (value: T) => void
  /** Names the switcher for assistive tech, e.g. "Finance sections". */
  label: string
  /** What the active item IS. `page` (the default) for a switcher between
   *  views; `location` for links to sections of ONE page, where the active
   *  item is where the reader is scrolled to rather than what is shown. */
  current?: 'page' | 'location'
  className?: string
}

/* Base UI (`@base-ui/react`): `Tabs` considered and rejected. `Tabs.Tab` renders `role="tab"`;
   this rail is deliberately plain buttons with `aria-current="page"` — a
   switcher, not a tablist — as recorded at
   QuoteDetailPage.participants.test.tsx:119-125 and queried as `role="button"`
   by that test and by QuoteWizardPage.test.tsx. The quote page's sticky section
   bar (S-3) is the same rail over links within ONE page, so it passes
   `current="location"` and its buttons scroll rather than swap. */

/* The underline section switcher used by Money, Directories and Settings.
 *
 * All three pages previously inlined this markup character-for-character. It is
 * one component now because the active marker animates: a slide only reads as
 * correct when every tab in a bar shares one marker element, which is not
 * something three separate copies can be trusted to keep true.
 *
 * The marker is a single element moved by Framer's layout engine via layoutId,
 * rather than a per-tab `after:` pseudo-element toggled between transparent and
 * brand. That is the whole trick: with one shared element Framer interpolates
 * its position between the old tab and the new one, so the underline travels
 * the distance instead of blinking out here and in over there.
 *
 * useId scopes that layoutId to this instance. Two tab bars mounted at once —
 * a page switcher and a card's own tabs — sharing a literal string would make
 * Framer animate one marker BETWEEN them, flying across the page.
 */
export function TabNav<T extends string>({
  items,
  value,
  onChange,
  label,
  current = 'page',
  className,
}: TabNavProps<T>) {
  const { t } = useTranslation()
  const markerId = useId()

  return (
    <nav
      aria-label={label}
      // overflow-y is pinned to hidden because a box with one axis scrollable
      // computes the other to auto, and the active marker sat 1px past the
      // bottom edge — enough to raise a scrollbar on a bar that never scrolls
      // vertically. The marker now sits at bottom-0 so hidden cannot clip it.
      className={cn(
        'border-line asa-scroll flex gap-1 overflow-x-auto overflow-y-hidden border-b',
        className,
      )}
    >
      {items.map((item) => {
        const active = item.key === value
        const text = item.labelKey ? t(item.labelKey) : item.label

        return (
          <button
            key={item.key}
            type="button"
            aria-current={active ? current : undefined}
            onClick={() => onChange(item.key)}
            className={cn(
              // min-h rather than a fixed height: a tab grows to the 44px
              // touch minimum on a coarse pointer (UX-DR-27) without capping
              // a longer Russian label to one line.
              'relative flex shrink-0 cursor-pointer items-center gap-1.5 px-3 py-2.5',
              'pointer-coarse:min-h-11',
              'text-[13px] transition-colors duration-[var(--dur-fast)]',
              'ease-[var(--ease-standard)]',
              active
                ? 'text-text-heading font-medium'
                : 'text-text-muted hover:text-text-heading font-normal',
            )}
          >
            {text}

            {item.badge === true ? (
              /* A marker with no count. Two SHAPES as well as two colours —
                 filled for attention, a ring for neutral — and the words in
                 `badgeLabel`, so neither the hue nor the glyph is the only
                 carrier of what it means. */
              <span
                aria-hidden
                title={item.badgeLabel}
                className={cn(
                  'size-2 shrink-0 rounded-full',
                  item.badgeTone === 'attention'
                    ? 'bg-attention'
                    : 'border-text-muted border-[1.5px]',
                )}
              />
            ) : item.badge ? (
              <span
                aria-hidden={item.badgeLabel ? true : undefined}
                title={item.badgeLabel}
                className={cn(
                  'asa-num rounded-full px-1.5 py-px text-[11px] font-semibold',
                  item.badgeTone === 'attention'
                    ? 'bg-attention-soft text-attention'
                    : 'bg-neutral-100 text-text-muted',
                )}
              >
                {item.badge}
              </span>
            ) : null}
            {/* The comma is spoken as a pause: without it the label and the
                badge's words run together into one word. */}
            {item.badge && item.badgeLabel ? (
              <span className="sr-only">{`, ${item.badgeLabel}`}</span>
            ) : null}

            {/* Rendered only under the active tab. Its absence elsewhere is
                what lets layoutId treat it as ONE element that moved, which is
                what produces the slide. The inset matches the `after:inset-x-2`
                of the markup this replaces, so the underline is the same width
                it has always been.

                bottom-0 rather than the original bottom-[-1px]: the marker has
                to stay inside the padding box now that the bar clips vertical
                overflow. It sits ON the hairline instead of 1px below it — at
                2px tall against a 1px border the difference is invisible. */}
            {active ? (
              <motion.span
                layoutId={markerId}
                transition={TAB_MARKER_TRANSITION}
                className="bg-brand absolute inset-x-2 bottom-0 h-0.5 rounded-full"
              />
            ) : null}
          </button>
        )
      })}
    </nav>
  )
}
